import {
  GAS_CAP_TOTAL_2015,
  GAS_CAP_TOTAL_2019,
  GAS_COM_CAP_2015,
  GAS_COM_CAP_2019,
  GAS_RES_MODEL_2015,
  GAS_RES_MODEL_2019,
} from './bhSourceControls';
import type { BhExperimentLegacyContext } from './legacyContext';
import { experimentYears, lerp } from './piecewiseActivity';
import { soCalGasObservedByYear } from './sceSoCalObservedComponents';

export const GAS_ASSUMPTION_UNREPORTED_NEGLIGIBLE = 'unreportedIndustrialAndOtherAssumedNegligible';

export const GAS_RES_COM_ASSUMPTION_CALLOUT =
  'Assumption for this experiment: SoCalGas residential and commercial categories capture all material community natural-gas use. Industrial customers may be absent or included within the commercial category. No separate industrial or unallocated amount is added.';

/** Beverly Hills commercial gas chart — observed vs modeled (legend matches bar colors). */
export const GAS_BH_COMMERCIAL_CHART_CALLOUT =
  'Commercial natural gas uses SoCalGas’s commercial-category therms where filed (2021–2023: utility observed, green). Earlier years are modeled: 2015 and 2019 commercial = CAP-derived community gas total minus the residential estimate at that year (dark blue); 2016–2018 and 2020 bridge between those anchors and utility years (light blue); 2005–2014 rescale the ClimatePlans commercial physical model to meet the 2015 anchor (purple). Industrial gas is not reported separately for Beverly Hills.';

export type GasComponentProvenance =
  | 'cap_calibrated_estimate'
  | 'model_backcast'
  | 'model_interpolated'
  | 'utility_reported';

export interface GasResidentialCommercialYear {
  year: number;
  residential: number;
  commercialCategory: number;
  total: number;
  residentialProvenance: GasComponentProvenance;
  commercialProvenance: GasComponentProvenance;
  bridgeMethod2020?: 'linear_sector_bridge';
}

function requireModel(map: Map<number, number>, year: number, label: string): number {
  const v = map.get(year);
  if (v == null || !Number.isFinite(v) || v <= 0) {
    throw new Error(`Missing or nonpositive ClimatePlans physical gas ${label} model at ${year}`);
  }
  return v;
}

function logInterpolateK(
  year: number,
  y0: number,
  anchor0: number,
  model0: number,
  y1: number,
  anchor1: number,
  model1: number,
  modelY: number
): number {
  const k0 = anchor0 / model0;
  const k1 = anchor1 / model1;
  const t = (year - y0) / (y1 - y0);
  const lnK = Math.log(k0) + t * (Math.log(k1) - Math.log(k0));
  return modelY * Math.exp(lnK);
}

export function buildGasResidentialCommercialByYear(
  legacy: BhExperimentLegacyContext
): Map<number, GasResidentialCommercialYear> {
  const out = new Map<number, GasResidentialCommercialYear>();
  const resModel = legacy.physicalGasRes;
  const comModel = legacy.physicalGasCom;

  const res2015 = GAS_RES_MODEL_2015;
  const com2015 = GAS_COM_CAP_2015;
  const res2019 = GAS_RES_MODEL_2019;
  const com2019 = GAS_COM_CAP_2019;

  const mRes2015 = requireModel(resModel, 2015, 'residential');
  const mCom2015 = requireModel(comModel, 2015, 'commercial');

  for (const year of experimentYears()) {
    const obs = soCalGasObservedByYear(year);
    if (obs && year >= 2021) {
      const total = obs.residential + obs.commercial;
      out.set(year, {
        year,
        residential: obs.residential,
        commercialCategory: obs.commercial,
        total,
        residentialProvenance: 'utility_reported',
        commercialProvenance: 'utility_reported',
      });
      continue;
    }

    if (year === 2015) {
      out.set(year, {
        year,
        residential: res2015,
        commercialCategory: com2015,
        total: GAS_CAP_TOTAL_2015,
        residentialProvenance: 'cap_calibrated_estimate',
        commercialProvenance: 'cap_calibrated_estimate',
      });
      continue;
    }

    if (year === 2019) {
      out.set(year, {
        year,
        residential: res2019,
        commercialCategory: com2019,
        total: GAS_CAP_TOTAL_2019,
        residentialProvenance: 'cap_calibrated_estimate',
        commercialProvenance: 'cap_calibrated_estimate',
      });
      continue;
    }

    if (year < 2015) {
      const mResY = requireModel(resModel, year, 'residential');
      const mComY = requireModel(comModel, year, 'commercial');
      const residential = mResY * (res2015 / mRes2015);
      const commercialCategory = mComY * (com2015 / mCom2015);
      out.set(year, {
        year,
        residential,
        commercialCategory,
        total: residential + commercialCategory,
        residentialProvenance: 'model_backcast',
        commercialProvenance: 'model_backcast',
      });
      continue;
    }

    if (year > 2015 && year < 2019) {
      const mResY = requireModel(resModel, year, 'residential');
      const mComY = requireModel(comModel, year, 'commercial');
      const mRes2019 = requireModel(resModel, 2019, 'residential');
      const mCom2019 = requireModel(comModel, 2019, 'commercial');
      const residential = logInterpolateK(year, 2015, res2015, mRes2015, 2019, res2019, mRes2019, mResY);
      const commercialCategory = logInterpolateK(
        year,
        2015,
        com2015,
        mCom2015,
        2019,
        com2019,
        mCom2019,
        mComY
      );
      out.set(year, {
        year,
        residential,
        commercialCategory,
        total: residential + commercialCategory,
        residentialProvenance: 'model_interpolated',
        commercialProvenance: 'model_interpolated',
      });
      continue;
    }

    if (year === 2020) {
      const obs21 = soCalGasObservedByYear(2021);
      if (!obs21) {
        throw new Error('Missing 2021 SoCalGas observation for 2020 gas bridge');
      }
      const residential = lerp(2020, 2019, res2019, 2021, obs21.residential);
      const commercialCategory = lerp(2020, 2019, com2019, 2021, obs21.commercial);
      out.set(year, {
        year,
        residential,
        commercialCategory,
        total: residential + commercialCategory,
        residentialProvenance: 'model_interpolated',
        commercialProvenance: 'model_interpolated',
        bridgeMethod2020: 'linear_sector_bridge',
      });
      continue;
    }

    throw new Error(`Unhandled gas year ${year} in residential+commercial builder`);
  }

  return out;
}
